// Google Analytics Service for client-side consumption
const GA4_FUNCTIONS_BASE_URL = 'https://us-central1-ozmonunmaceradunyasi.cloudfunctions.net';

// Helper function to make API calls
const makeAPICall = async (endpoint, params = {}) => {
  try {
    const queryString = new URLSearchParams(params).toString();
    const url = `${GA4_FUNCTIONS_BASE_URL}/${endpoint}${queryString ? `?${queryString}` : ''}`;
    
    console.log(`Making API call to: ${url}`);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    
    console.log(`Response status: ${response.status}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    console.log(`API response:`, data);
    return data;
  } catch (error) {
    console.error(`Error calling ${endpoint}:`, error);
    throw error;
  }
};

// Get basic analytics stats
export const getGA4AnalyticsStats = async (startDate = '7daysAgo', endDate = 'today') => {
  return await makeAPICall('getAnalyticsStats', { startDate, endDate });
};

// Get daily active users over time
export const getGA4DailyActiveUsers = async (days = 30) => {
  return await makeAPICall('getDailyActiveUsers', { days });
};

// Get device and platform analytics
export const getGA4DeviceAnalytics = async (startDate = '7daysAgo', endDate = 'today') => {
  return await makeAPICall('getDeviceAnalytics', { startDate, endDate });
};

// Get page/screen analytics
export const getGA4PageAnalytics = async (startDate = '7daysAgo', endDate = 'today', limit = 10) => {
  return await makeAPICall('getPageAnalytics', { startDate, endDate, limit });
};

// Get user acquisition analytics
export const getGA4UserAcquisition = async (startDate = '7daysAgo', endDate = 'today') => {
  return await makeAPICall('getUserAcquisition', { startDate, endDate });
};

// Get real-time analytics
export const getGA4RealTimeAnalytics = async () => {
  return await makeAPICall('getRealTimeAnalytics');
};

// Get comprehensive analytics dashboard data
export const getGA4AnalyticsDashboard = async (startDate = '7daysAgo', endDate = 'today') => {
  return await makeAPICall('getAnalyticsDashboard', { startDate, endDate });
};

// Format GA4 data for your existing analytics components
export const formatGA4DataForDashboard = (ga4Data) => {
  if (!ga4Data?.success || !ga4Data?.data) {
    return null;
  }

  const { data } = ga4Data;
  
  return {
    // Basic stats
    totalActiveUsers: data.basicStats?.activeUsers || 0,
    totalNewUsers: data.basicStats?.newUsers || 0,
    totalSessions: data.basicStats?.sessions || 0,
    totalPageViews: data.basicStats?.pageViews || 0,
    averageSessionDuration: data.basicStats?.avgSessionDuration || 0,
    bounceRate: data.basicStats?.bounceRate || 0,
    
    // Daily users chart data
    dailyActiveUsers: data.dailyUsers?.map(day => ({
      date: day.date,
      count: day.activeUsers
    })) || [],
    
    // Device breakdown
    deviceStats: data.deviceStats?.map(device => ({
      device: device.deviceCategory,
      users: device.activeUsers,
      sessions: device.sessions
    })) || [],
    
    // Top pages
    topPages: data.topPages?.map(page => ({
      page: page.pagePath,
      views: page.pageViews,
      users: page.activeUsers
    })) || [],
    
    // User acquisition
    userAcquisition: data.userAcquisition?.map(source => ({
      source: source.source,
      newUsers: source.newUsers,
      activeUsers: source.activeUsers
    })) || []
  };
};

// Get GA4 data and merge with existing Firebase data
export const getCombinedAnalytics = async (startDate, endDate) => {
  try {
    const [ga4Data, firebaseData] = await Promise.all([
      getGA4AnalyticsDashboard(startDate, endDate),
      // Your existing Firebase analytics functions
      // getDailySessionStats(startDate, endDate),
      // getWeeklyStats(),
      // etc.
    ]);
    
    const formattedGA4Data = formatGA4DataForDashboard(ga4Data);
    
    return {
      ga4: formattedGA4Data,
      firebase: firebaseData,
      combined: {
        // Merge GA4 and Firebase data where appropriate
        totalActiveUsers: formattedGA4Data?.totalActiveUsers || 0,
        totalSessions: formattedGA4Data?.totalSessions || 0,
        // Add more combined metrics as needed
      }
    };
  } catch (error) {
    console.error('Error getting combined analytics:', error);
    return {
      ga4: null,
      firebase: null,
      error: error.message
    };
  }
};
